import cx from "classnames";
import { Location } from "history";
import { parse, stringify } from "query-string";
import React from "react";
import Helmet from "react-helmet";
import { Index, List, ListRowProps } from "react-virtualized";

import FeedPost from "@/mk2/apps/forum/components/FeedPost";
import { FeedPostPlaceholder } from "@/mk2/apps/forum/components/FeedPostPlaceholder";
import { ComplaintsStatisticsPerPost, PostEntity } from "@/mk2/apps/forum/schemas";
import { isCounsellingPost } from "@/mk2/apps/forum/utils";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { InfiniteListLoadable } from "@/mk2/components/InfiniteListLoadable";
import { NoResults } from "@/mk2/components/NoResults";
import { Failure } from "@/mk2/components/errors/Failure";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import { LIST_OF_GROUPS_SHOW_MORE, LOAD_FEED_NEWEST } from "@/mk/autogenerated/translation";

import styles from "./Feed.mscss";

interface OwnProps {
    className?: string;
    posts: PostEntity[];
    after: number;
    hasNext: number;
    hasPrevious: number;
    baseUrl: string;
    loadingState: LoadingState;
    errorMessage: string;
    complaintsStatistics: ComplaintsStatisticsPerPost;
    isMobile: boolean;
    location: Location;
    noResults?: React.ReactNode;
    noResultsDetail?: string;
    backButton?: React.ReactNode;
    staticContext?: any;
    readonlyPosts: boolean;
    usePaging?: boolean;

    showTopic?: boolean;
    showSticky?: boolean;
    showCreated?: boolean;

    inFeed?: boolean;

    onLoad(after?: number);
}

type Props = OwnProps;

interface State {
    loadingPrevious: boolean;
    loadingNext: boolean;
}

const url = (base: string, params: any) => {
    const search = stringify(params);
    return base + (search ? `?${search}` : "");
};

class Feed extends React.Component<Props, State> {
    private dispose: () => void;
    private infiniteList: List;

    constructor(props, context) {
        super(props, context);

        this.state = {
            loadingPrevious: false,
            loadingNext: false,
        };
    }

    public shouldComponentUpdate(n: Props, ns: State) {
        const p = this.props;
        const ps = this.state;

        return (
            p.posts !== n.posts ||
            p.loadingState !== n.loadingState ||
            p.complaintsStatistics !== n.complaintsStatistics ||
            ps.loadingPrevious !== ns.loadingPrevious ||
            ps.loadingNext !== ns.loadingNext
        );
    }

    public UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // Going from loading to success -> so reset loadingPrevious and loadingNext state
        if (
            nextProps.loadingState !== this.props.loadingState &&
            nextProps.loadingState === LoadingState.SUCCESS
        ) {
            this.setState({
                loadingPrevious: false,
                loadingNext: false,
            });
        }
    }

    public componentDidMount() {
        const listener = () => {
            window.requestAnimationFrame(() => {
                this.forceUpdate();
            });
        };

        window.addEventListener("resize", listener, false);

        this.dispose = () => {
            window.removeEventListener("resize", listener, false);
        };
    }

    public componentDidUpdate(prevProps: OwnProps) {
        // After has changed
        if (this.props.after !== prevProps.after) {
            this.load();
        }

        for (let index = 0; index < this.props.posts.length; index++) {
            // Out of range
            if (prevProps.posts.length <= index) {
                break;
            }

            // Posts were re-ordered - force recompute row heights
            if (
                this.infiniteList &&
                prevProps.posts.length > index &&
                prevProps.posts[index].id !== this.props.posts[index].id
            ) {
                this.infiniteList.recomputeRowHeights(index);
                break;
            }
        }
    }

    public componentWillUnmount() {
        if (this.dispose) {
            this.dispose();
        }
    }

    public render() {
        const { loadingState, className, posts } = this.props;

        return (
            <div className={cx(styles.Feed, className)}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderInit={this.renderInit}
                    onRenderSuccess={this.renderSuccess}
                    onRenderFailure={this.renderFailure}
                    hasData={posts && posts.length > 0}
                />
            </div>
        );
    }

    private renderInit = () => {
        const { isMobile } = this.props;

        return (
            <div className={styles.Feed__loadingInit}>
                <FeedPostPlaceholder isMobile={isMobile} lengths="v1" />
                <FeedPostPlaceholder isMobile={isMobile} lengths="v2" />
                <FeedPostPlaceholder isMobile={isMobile} lengths="v3" />
                <FeedPostPlaceholder isMobile={isMobile} lengths="v1" />
                <FeedPostPlaceholder isMobile={isMobile} lengths="v2" />
                <FeedPostPlaceholder isMobile={isMobile} lengths="v3" />
            </div>
        );
    };

    private renderFailure = () => {
        const { staticContext, errorMessage, backButton } = this.props;

        return (
            <Failure
                staticContext={staticContext}
                errorMessage={errorMessage}
                onRetry={this.load}
                backButton={backButton}
            />
        );
    };

    private renderSuccess = () => {
        const {
            posts,
            loadingState,
            isMobile,
            hasPrevious,
            hasNext,
            baseUrl,
            location,
            noResults,
            noResultsDetail,
            usePaging,
        } = this.props;

        // InfiniteScroll data
        // @see https://github.com/bvaughn/react-virtualized/blob/master/docs/creatingAnInfiniteLoadingList.md
        const rowCount = hasNext ? posts.length + 1 : posts.length;

        // hashtags in the URL as "h=something" is allowed
        const parsed = parse(location.search);

        return (
            <div className={styles.Feed__loadingSuccess}>
                <Helmet>
                    {hasNext && (
                        <link
                            rel="next"
                            href={
                                baseUrl +
                                url(location.pathname, {
                                    after: posts[posts.length - 1].id,
                                    h: parsed.h,
                                })
                            }
                        />
                    )}
                    {hasPrevious && (
                        <link
                            rel="prev"
                            href={
                                baseUrl +
                                url(location.pathname, { after: -posts[0].id, h: parsed.h })
                            }
                        />
                    )}
                </Helmet>

                {hasPrevious && (
                    <div className={styles.Feed__loadPrevious}>
                        <Btn
                            className={styles.Feed__loadPrevious__link}
                            type={BtnType.Link}
                            link={{ ...location, search: "" }}
                            onClick={this.loadPrevious}
                            hasSpinner={this.state.loadingPrevious}
                            label={LOAD_FEED_NEWEST}
                        />
                    </div>
                )}

                {posts.length ? (
                    usePaging ? (
                        <>
                            {Array.from(Array(posts.length).keys()).map((index) =>
                                this.renderRow({
                                    index,
                                    key: index.toString(),
                                    // Ignored props
                                    style: {},
                                    columnIndex: 0,
                                    isScrolling: null,
                                    isVisible: null,
                                    parent: null,
                                }),
                            )}

                            {hasNext && (
                                <div className={styles.Feed__loadNextPage}>
                                    <Btn
                                        className={styles.Feed__loadNextPage__link}
                                        type={BtnType.Link}
                                        // link={{ ...location, search: '' }}
                                        onClick={this.loadNext}
                                        hasSpinner={this.state.loadingNext}
                                        label={LIST_OF_GROUPS_SHOW_MORE}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <InfiniteListLoadable
                            listRef={this.setInfiniteList}
                            isRowLoaded={this.isRowLoaded}
                            loadMoreRows={this.loadNext}
                            rowCount={rowCount}
                            rowRenderer={this.renderRow}
                            rowHeight={this.computeRowHeight}
                            estimatedRowHeight={FeedPost.outerHeightMax(isMobile)}
                        />
                    )
                ) : (
                    noResults &&
                    isSuccess(loadingState) && (
                        <div className={styles.Feed__noResults}>
                            <NoResults message={noResults} detailMessage={noResultsDetail} noIcon />
                        </div>
                    )
                )}
            </div>
        );
    };

    private load = () => {
        const { onLoad, after } = this.props;
        onLoad(after);
    };

    private computeRowHeight = (params: Index) => {
        const { posts, isMobile } = this.props;

        if (!this.isRowLoaded(params)) {
            return FeedPostPlaceholder.outerHeight(isMobile);
        } else {
            const post = posts[params.index];
            return FeedPost.outerHeight(post, isMobile);
        }
    };

    /**
     * Fire this when we should load more posts during infinite scroll (or "show more" when using paging)
     */
    private loadNext = () => {
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        this.setState({ loadingNext: true });

        const { posts, onLoad } = this.props;
        const after = posts[posts.length - 1].id;
        onLoad(after);

        return new Promise((resolve) => {
            setTimeout(resolve, 500);
        });
    };

    private loadPrevious = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        this.setState({
            loadingPrevious: true,
        });

        const { onLoad } = this.props;
        onLoad();
    };

    private isRowLoaded = ({ index }) => !this.props.hasNext || index < this.props.posts.length;

    private renderRow = (listRowProps: ListRowProps) => {
        const {
            posts,
            complaintsStatistics,
            isMobile,
            showTopic,
            showSticky,
            showCreated,
            readonlyPosts,
            inFeed,
        } = this.props;
        const { index, key, style } = listRowProps;

        const post: PostEntity | null = posts[index] || null; // null ak este nemame post loadnuty
        const isCounselling = isCounsellingPost(post);

        return (
            // wrapper element kde react-virtualized applikuje svoje vypocitane style (height, width, position)
            // nesmie mat margin
            <div key={key} style={style}>
                {!this.isRowLoaded({ index }) ? (
                    <FeedPostPlaceholder isMobile={isMobile} />
                ) : (
                    <FeedPost
                        post={post}
                        isMobile={isMobile}
                        showTopic={showTopic}
                        showSticky={showSticky}
                        showCreated={showCreated}
                        complaintsCount={
                            complaintsStatistics[post.id] ? complaintsStatistics[post.id].count : 0
                        }
                        readonlyPosts={readonlyPosts}
                        showAuthor={!isCounselling}
                        showCheckMark={isCounselling}
                        showFullTitle={false}
                        alignDateLeft={isCounselling && !inFeed}
                    />
                )}
            </div>
        );
    };

    private setInfiniteList = (ref) => (this.infiniteList = ref);
}

export default Feed;
